import type { IfcSchemaVersion } from '@viewer/store/viewerSlice'
import { viewerApi } from '@viewer/lib/viewerApi'
import { rawIfc } from '@viewer/lib/rawIfc'
import { useAppStore } from '@core/store'
import { markWorkDirty } from '@core/session/recovery'

const DB_NAME = 'vn2r.model-history'
const DB_VERSION = 1
const STORE = 'milestones'

export interface ModelMilestone {
  id: string
  label: string
  notes: string
  sourceFilename: string
  sourceSha256: string
  schemaVersion: IfcSchemaVersion | null
  createdAt: string
  sizeBytes: number
  /** O que está guardado. Um marco guarda o IFC sempre que ele existe:
   *  guardar só o Fragments dava um marco que se vê em 3D mas que o
   *  Comparar IFC não sabe ler, e comparar era a razão de o guardar.
   *  'frag' é o recurso para um modelo que já veio sem IFC. */
  payloadFormat: 'ifc' | 'frag'
  fragments: Blob
}

/** A extensão do que está no marco. Registos antigos não dizem o
 *  formato e são Fragments, que era tudo o que se guardava. */
function payloadExtension(record: ModelMilestone): 'ifc' | 'frag' {
  return record.payloadFormat === 'ifc' ? 'ifc' : 'frag'
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      if (request.result.objectStoreNames.contains(STORE)) return
      const store = request.result.createObjectStore(STORE, { keyPath: 'id' })
      store.createIndex('byCreatedAt', 'createdAt')
      store.createIndex('bySourceSha', 'sourceSha256')
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => {
      dbPromise = null
      reject(request.error ?? new Error('IDB open failed'))
    }
  })
  return dbPromise
}

function requestPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IDB request failed'))
  })
}

async function transaction<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openDb()
  return await new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    let result: T
    run(tx.objectStore(STORE)).then((value) => (result = value), reject)
    tx.oncomplete = () => resolve(result)
    tx.onerror = () => reject(tx.error ?? new Error('IDB transaction failed'))
    tx.onabort = () => reject(tx.error ?? new Error('IDB transaction aborted'))
  })
}

export function milestoneFilename(
  sourceFilename: string,
  label: string,
  extension: 'ifc' | 'frag' = 'frag',
): string {
  const base = sourceFilename.replace(/\.(ifc|ifczip|frag|fragments)$/i, '')
  const suffix = label
    .normalize('NFKD')
    // Tirar os acentos, e não parti-los. Sem isto o NFKD separa o "ã"
    // em "a" mais um sinal que não passa no filtro seguinte, e
    // "revisão" saía "revisa-o". Num produto em português isso é o
    // caso normal, não o caso raro.
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\w.-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
  return `${base}-${suffix || 'versao'}.${extension}`
}

export const modelHistory = {
  async capture(
    modelId: string,
    label: string,
    notes = '',
  ): Promise<ModelMilestone> {
    const model = useAppStore.getState().models.find((entry) => entry.id === modelId)
    if (!model) throw new Error('O modelo já não está carregado.')
    // Guardar o IFC, e não a sua derivação, sempre que ele exista: um
    // marco serve para comparar, e o Comparar IFC lê o IFC. Reabrir sai
    // barato na mesma, porque a cache de Fragments reconhece o mesmo
    // SHA e salta a conversão.
    const ifcBytes = rawIfc.getCachedBytes(modelId)
    const payloadFormat: 'ifc' | 'frag' = ifcBytes ? 'ifc' : 'frag'
    const buffer: ArrayBuffer = ifcBytes
      ? (ifcBytes.slice().buffer as ArrayBuffer)
      : await viewerApi.getFragmentsBuffer(modelId)
    const record: ModelMilestone = {
      id: crypto.randomUUID(),
      label: label.trim() || `Versão ${new Date().toLocaleString('pt-PT')}`,
      notes: notes.trim(),
      sourceFilename: model.fileName,
      sourceSha256: model.sha256,
      schemaVersion: model.schemaVersion,
      createdAt: new Date().toISOString(),
      sizeBytes: buffer.byteLength,
      payloadFormat,
      fragments: new Blob([buffer], { type: 'application/octet-stream' }),
    }
    await transaction('readwrite', (store) => requestPromise(store.put(record)))
    markWorkDirty({ ifcName: model.fileName })
    return record
  },

  async list(): Promise<ModelMilestone[]> {
    try {
      const rows = await transaction('readonly', (store) =>
        requestPromise(store.getAll() as IDBRequest<ModelMilestone[]>),
      )
      return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    } catch {
      return []
    }
  },

  async remove(id: string): Promise<void> {
    await transaction('readwrite', (store) => requestPromise(store.delete(id)))
  },

  async open(record: ModelMilestone, replace: boolean): Promise<void> {
    const file = new File(
      [record.fragments],
      milestoneFilename(
        record.sourceFilename,
        record.label,
        payloadExtension(record),
      ),
      { type: 'application/octet-stream' },
    )
    await viewerApi.loadIfcFile(file, { replace })
  },

  async clearAll(): Promise<void> {
    await transaction('readwrite', (store) => requestPromise(store.clear()))
  },
}

